// src/services/authService.js - VERSÃO ATUALIZADA COM PERMISSÕES
import AsyncStorage from '@react-native-async-storage/async-storage';

// URL base da API - SEMPRE APONTAR PARA HEROKU
const API_URL = 'https://avaqualis-server-5b5d8ca54885.herokuapp.com/api';

// Chaves do AsyncStorage
const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  AVALIADOR_DATA: 'avaliador_data',
  SESSION_EXPIRES: 'session_expires',
  LOGIN_TYPE: 'login_type', // 'visitor' ou 'authenticated'
};

class AuthService {
  constructor() {
    this.token = null;
    this.avaliador = null;
    this.isVisitor = true; // Começa como visitante por padrão
  }

  // Inicializar serviço - SEMPRE como visitante
  async initialize() {
    try {
      console.log('🔐 AuthService: Inicializando...');

      const token = await AsyncStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const avaliadorData = await AsyncStorage.getItem(STORAGE_KEYS.AVALIADOR_DATA);
      const loginType = await AsyncStorage.getItem(STORAGE_KEYS.LOGIN_TYPE);
      const sessionExpires = await AsyncStorage.getItem(STORAGE_KEYS.SESSION_EXPIRES);

      console.log('🔐 AuthService: Dados carregados do storage:', {
        hasToken: !!token,
        hasAvaliadorData: !!avaliadorData,
        loginType,
        sessionExpires,
      });

      // Se tem token e dados válidos de usuário autenticado
      if (token && avaliadorData && sessionExpires && loginType === 'authenticated') {
        const expiresAt = new Date(sessionExpires);
        if (expiresAt > new Date()) {
          // Parsear os dados do avaliador
          const parsedAvaliador = JSON.parse(avaliadorData);
          console.log('🔐 AuthService: Dados do avaliador carregados do storage:', {
            id: parsedAvaliador.id,
            nome: parsedAvaliador.nome,
            level: parsedAvaliador.level,
            email: parsedAvaliador.email,
          });

          // Verificar se a sessão ainda é válida no servidor
          try {
            const response = await fetch(`${API_URL}/auth/verify-session`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const data = await response.json();
              console.log('🔐 AuthService: Resposta da verificação de sessão:', data);

              this.token = token;
              // IMPORTANTE: Usar dados do servidor, mas manter o level do storage se não vier do servidor
              this.avaliador = {
                ...parsedAvaliador,
                ...data.avaliador,
                level: data.avaliador?.level || parsedAvaliador.level,
              };
              this.isVisitor = false;

              // IMPORTANTE: Garantir que o level está presente
              console.log('✅ AuthService: Sessão restaurada:', {
                id: this.avaliador.id,
                nome: this.avaliador.nome,
                level: this.avaliador.level,
                email: this.avaliador.email,
              });

              return { success: true, avaliador: this.avaliador };
            } else {
              console.log('⚠️ AuthService: Sessão expirada no servidor');
            }
          } catch (error) {
            console.log('⚠️ AuthService: Erro ao verificar sessão:', error.message);
          }
        } else {
          console.log('⚠️ AuthService: Sessão expirada localmente');
        }
      }
    } catch (error) {
      console.error('❌ AuthService: Erro ao inicializar:', error);
    }

    // Em qualquer caso de falha, fazer login como visitante
    console.log('🔐 AuthService: Fazendo login como visitante...');
    await this.loginAsVisitor();
    return { success: true, isVisitor: true };
  }

  // Login como visitante
  async loginAsVisitor() {
    try {
      this.isVisitor = true;
      this.avaliador = {
        id: 'visitor',
        nome: 'Visitante',
        email: 'visitante@avaqualis.com',
        level: 'visitor',
      };
      this.token = null;

      await AsyncStorage.setItem(STORAGE_KEYS.LOGIN_TYPE, 'visitor');
      await AsyncStorage.setItem(STORAGE_KEYS.AVALIADOR_DATA, JSON.stringify(this.avaliador));
      await AsyncStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      await AsyncStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRES);

      console.log('✅ AuthService: Login como visitante realizado');
      return { success: true };
    } catch (error) {
      console.error('❌ AuthService: Erro ao fazer login como visitante:', error);
      return { success: false, error: error.message };
    }
  }

  // Solicitar código de verificação
  async requestVerificationCode(avaliadorId) {
    try {
      console.log(`📧 AuthService: Solicitando código para avaliador ${avaliadorId}...`);
      console.log(`🌐 AuthService: Enviando para: ${API_URL}/auth/request-code`);

      const response = await fetch(`${API_URL}/auth/request-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ avaliadorId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao solicitar código');
      }

      console.log('✅ AuthService: Resposta do servidor:', data);

      // Se estiver em desenvolvimento e o código foi retornado
      if (data.devCode) {
        console.log(`🔑 CÓDIGO DE DESENVOLVIMENTO: ${data.devCode}`);
      }

      // Se houve erro no email mas o código foi gerado
      if (data.emailError) {
        console.warn('⚠️ AuthService: Email não foi enviado, mas código foi gerado');
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('❌ AuthService: Erro ao solicitar código:', error);
      return { success: false, error: error.message };
    }
  }

  // Verificar código e fazer login
  // Verificar código e fazer login
  async verifyCode(avaliadorId, code) {
    try {
      console.log(`🔐 AuthService: Verificando código para avaliador ${avaliadorId}...`);

      // NOVO: Importar e verificar se é código curinga
      const { getCuringaCode } = await import('../data/EstruturaOrganizacional');
      const curingaCode = getCuringaCode(avaliadorId);

      // Se for código curinga, fazer login direto sem validar no servidor
      if (curingaCode && code === curingaCode) {
        console.log(`🔑 AuthService: Código curinga detectado para ${avaliadorId}`);

        // Buscar dados do avaliador localmente
        const { AVALIADORES } = await import('../data/EstruturaOrganizacional');
        const avaliadorLocal = AVALIADORES.find(a => a.id === avaliadorId);

        if (!avaliadorLocal) {
          throw new Error('Avaliador não encontrado');
        }

        // Criar token simulado (apenas para modo curinga)
        const fakeToken = `curinga_${avaliadorId}_${Date.now()}`;
        const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 dias

        // Salvar dados da sessão
        this.token = fakeToken;
        this.avaliador = {
          id: avaliadorLocal.id,
          nome: avaliadorLocal.nome,
          email: avaliadorLocal.login,
          level: avaliadorLocal.level,
        };
        this.isVisitor = false;

        await AsyncStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, fakeToken);
        await AsyncStorage.setItem(STORAGE_KEYS.AVALIADOR_DATA, JSON.stringify(this.avaliador));
        await AsyncStorage.setItem(STORAGE_KEYS.SESSION_EXPIRES, expiresAt);
        await AsyncStorage.setItem(STORAGE_KEYS.LOGIN_TYPE, 'authenticated');

        console.log('✅ AuthService: Login com código curinga bem-sucedido');

        return {
          success: true,
          token: fakeToken,
          avaliador: this.avaliador,
          expiresAt: expiresAt,
          expiresIn: 30 * 24 * 60 * 60, // 30 dias em segundos
        };
      }

      // Se não for curinga, prosseguir com validação normal no servidor
      console.log(`🌐 AuthService: Enviando para: ${API_URL}/auth/verify-code`);

      const response = await fetch(`${API_URL}/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ avaliadorId, code }),
      });

      const data = await response.json();
      console.log('📥 AuthService: Resposta completa do servidor:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao verificar código');
      }

      console.log('✅ AuthService: Login bem-sucedido, dados recebidos:', {
        avaliador: data.avaliador,
        hasToken: !!data.token,
        expiresAt: data.expiresAt,
      });

      // IMPORTANTE: Se o level não veio do servidor, buscar localmente
      if (data.avaliador && !data.avaliador.level) {
        console.warn(
          '⚠️ AuthService: Level não encontrado nos dados do servidor, tentando recuperar...'
        );

        // Buscar o level do avaliador local
        const { AVALIADORES } = await import('../data/EstruturaOrganizacional');
        const localAvaliador = AVALIADORES.find(a => a.id === avaliadorId);

        if (localAvaliador) {
          data.avaliador.level = localAvaliador.level;
          console.log('✅ AuthService: Level recuperado localmente:', localAvaliador.level);
        } else {
          console.error('❌ AuthService: Não foi possível recuperar o level do avaliador');
          data.avaliador.level = 'membro'; // Fallback para membro
        }
      }

      // Salvar dados da sessão
      this.token = data.token;
      this.avaliador = data.avaliador;
      this.isVisitor = false;

      console.log('💾 AuthService: Salvando no storage, avaliador:', {
        id: this.avaliador.id,
        nome: this.avaliador.nome,
        level: this.avaliador.level,
        email: this.avaliador.email,
      });

      await AsyncStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.token);
      await AsyncStorage.setItem(STORAGE_KEYS.AVALIADOR_DATA, JSON.stringify(data.avaliador));
      await AsyncStorage.setItem(STORAGE_KEYS.SESSION_EXPIRES, data.expiresAt);
      await AsyncStorage.setItem(STORAGE_KEYS.LOGIN_TYPE, 'authenticated');

      console.log('✅ AuthService: Dados salvos no storage, avaliador atual:', {
        id: this.avaliador.id,
        nome: this.avaliador.nome,
        level: this.avaliador.level,
        email: this.avaliador.email,
      });

      return { success: true, ...data };
    } catch (error) {
      console.error('❌ AuthService: Erro ao verificar código:', error);
      return { success: false, error: error.message };
    }
  }

  // Fazer logout - CORRIGIDO
  async logout() {
    try {
      console.log('🚪 AuthService: Iniciando logout completo...');

      // Se estiver autenticado, notificar o servidor
      if (this.token && !this.isVisitor) {
        try {
          await fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          });
        } catch (error) {
          console.log('⚠️ AuthService: Erro ao notificar logout ao servidor:', error);
        }
      }

      // Limpar TODOS os dados do AsyncStorage
      try {
        console.log('🧹 AuthService: Limpando todos os dados do AsyncStorage...');
        const allKeys = await AsyncStorage.getAllKeys();
        console.log(`📋 AuthService: Encontradas ${allKeys.length} chaves para limpar`);

        // Limpar todas as chaves
        await AsyncStorage.multiRemove(allKeys);
        console.log('✅ AuthService: Todos os dados do AsyncStorage foram limpos');

        // Limpar também os dados em memória
        this.token = null;
        this.avaliador = null;
        this.isVisitor = true;
      } catch (error) {
        console.error('❌ AuthService: Erro ao limpar AsyncStorage:', error);
      }

      // NÃO fazer login automático como visitante
      // Deixar o App.js recarregar a página

      console.log('✅ AuthService: Logout completo realizado');
      return { success: true };
    } catch (error) {
      console.error('❌ AuthService: Erro ao fazer logout:', error);
      return { success: false, error: error.message };
    }
  }

  // Verificar se está autenticado
  isAuthenticated() {
    const isAuth = this.avaliador !== null;
    console.log('🔐 AuthService: isAuthenticated:', isAuth, 'avaliador:', this.avaliador);
    return isAuth;
  }

  // Obter token atual
  getToken() {
    return this.token;
  }

  // Obter dados do avaliador
  getAvaliador() {
    console.log('🔐 AuthService: getAvaliador retornando:', {
      id: this.avaliador?.id,
      nome: this.avaliador?.nome,
      level: this.avaliador?.level,
      email: this.avaliador?.email,
    });
    return this.avaliador;
  }

  // Verificar se é visitante
  isVisitorMode() {
    return this.isVisitor;
  }

  // Obter headers de autorização
  getAuthHeaders() {
    if (this.token && !this.isVisitor) {
      return {
        Authorization: `Bearer ${this.token}`,
      };
    }
    return {};
  }

  // NOVA FUNÇÃO: Verificar se pode ver formulário específico
  canViewForm(form) {
    console.log('🔐 AuthService: Verificando permissão para visualizar formulário:', {
      formId: form?.id,
      avaliadorAtual: this.avaliador?.id,
      levelAtual: this.avaliador?.level,
      avaliador1: form?.metadata?.avaliador1,
      avaliador2: form?.metadata?.avaliador2,
    });

    if (!this.avaliador) {
      console.log('❌ AuthService: Nenhum avaliador logado');
      return false;
    }

    // Visitante não pode ver formulários
    if (this.isVisitor || this.avaliador.id === 'visitor') {
      console.log('❌ AuthService: Visitante não pode ver formulários');
      return false;
    }

    // Programador e coordenação podem ver todos os formulários
    if (this.avaliador.level === 'programador' || this.avaliador.level === 'coordenacao') {
      console.log('✅ AuthService: Programador/Coordenação pode ver todos os formulários');
      return true;
    }

    // Membros só podem ver formulários atribuídos a eles
    if (form && form.metadata) {
      const canView =
        form.metadata.avaliador1 === this.avaliador.id ||
        form.metadata.avaliador2 === this.avaliador.id;

      console.log(canView ? '✅' : '❌', 'AuthService: Membro pode ver formulário:', canView);
      return canView;
    }

    console.log('❌ AuthService: Formulário sem metadata ou dados insuficientes');
    return false;
  }

  // NOVA FUNÇÃO: Verificar se pode editar avaliadores de um formulário
  canEditEvaluators(form) {
    console.log('🔐 AuthService: Verificando permissão para editar avaliadores:', {
      avaliadorAtual: this.avaliador?.id,
      levelAtual: this.avaliador?.level,
    });

    if (!this.avaliador) {
      return false;
    }

    // Visitante não pode editar
    if (this.isVisitor || this.avaliador.id === 'visitor') {
      return false;
    }

    // Apenas programador e coordenação podem editar avaliadores
    const canEdit =
      this.avaliador.level === 'programador' || this.avaliador.level === 'coordenacao';

    console.log(canEdit ? '✅' : '❌', 'AuthService: Pode editar avaliadores:', canEdit);
    return canEdit;
  }

  // NOVA FUNÇÃO: Verificar se pode excluir formulário completo
  canDeleteForm() {
    console.log('🔐 AuthService: Verificando permissão para excluir formulário:', {
      avaliadorAtual: this.avaliador?.id,
      levelAtual: this.avaliador?.level,
    });

    if (!this.avaliador) {
      return false;
    }

    // Visitante não pode excluir
    if (this.isVisitor || this.avaliador.id === 'visitor') {
      return false;
    }

    // Apenas programador e coordenação podem excluir formulários
    const canDelete =
      this.avaliador.level === 'programador' || this.avaliador.level === 'coordenacao';

    console.log(canDelete ? '✅' : '❌', 'AuthService: Pode excluir formulário:', canDelete);
    return canDelete;
  }

  // NOVA FUNÇÃO: Verificar se pode excluir resposta específica
  canDeleteResponse(response, form) {
    console.log('🔐 AuthService: Verificando permissão para excluir resposta:', {
      responseId: response?.id,
      avaliadorAtual: this.avaliador?.id,
      levelAtual: this.avaliador?.level,
      avaliadorResposta: response?.avaliador?.id,
    });

    if (!this.avaliador) {
      return false;
    }

    // Visitante não pode excluir
    if (this.isVisitor || this.avaliador.id === 'visitor') {
      return false;
    }

    // Programador e coordenação podem excluir qualquer resposta
    if (this.avaliador.level === 'programador' || this.avaliador.level === 'coordenacao') {
      console.log('✅ AuthService: Programador/Coordenação pode excluir qualquer resposta');
      return true;
    }

    // Membros só podem excluir suas próprias respostas
    if (response && response.avaliador) {
      const canDelete = response.avaliador.id === this.avaliador.id;
      console.log(
        canDelete ? '✅' : '❌',
        'AuthService: Membro pode excluir própria resposta:',
        canDelete
      );
      return canDelete;
    }

    console.log('❌ AuthService: Resposta sem avaliador ou dados insuficientes');
    return false;
  }

  // Verificar se tem permissão para uma ação (atualizada)
  hasPermission(action) {
    console.log('🔐 AuthService: Verificando permissão:', action, 'para usuário:', {
      id: this.avaliador?.id,
      nome: this.avaliador?.nome,
      level: this.avaliador?.level,
    });

    if (this.isVisitor) {
      // Visitantes têm permissões limitadas
      const visitorPermissions = [
        'view_forms',
        'view_responses',
        'create_local_response',
        'edit_local_response',
      ];
      return visitorPermissions.includes(action);
    }

    // Verificar permissões específicas baseadas no level
    switch (action) {
      case 'create_form':
        return this.avaliador?.level === 'coordenacao' || this.avaliador?.level === 'programador';
      case 'view_simulation':
        return this.avaliador?.level === 'coordenacao' || this.avaliador?.level === 'programador';
      case 'edit_evaluators':
        return this.avaliador?.level === 'coordenacao' || this.avaliador?.level === 'programador';
      case 'delete_form':
        return this.avaliador?.level === 'coordenacao' || this.avaliador?.level === 'programador';
      case 'delete_any_response':
        return this.avaliador?.level === 'coordenacao' || this.avaliador?.level === 'programador';
      default:
        return true;
    }
  }

  // Obter tempo restante da sessão
  async getSessionTimeRemaining() {
    try {
      const sessionExpires = await AsyncStorage.getItem(STORAGE_KEYS.SESSION_EXPIRES);
      if (sessionExpires && !this.isVisitor) {
        const expiresAt = new Date(sessionExpires);
        const now = new Date();
        const diff = expiresAt - now;

        if (diff > 0) {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

          return { days, hours, minutes, total: diff };
        }
      }
      return null;
    } catch (error) {
      console.error('❌ AuthService: Erro ao calcular tempo restante:', error);
      return null;
    }
  }
}

// Exportar instância única do serviço
export default new AuthService();
